import { getLogger } from '../../../libs/logger';
import { StorageKeys } from '../../../shared/constants/storageKeys';

const logger = getLogger('chunkReload');

/**
 * Matches the dynamic-import / code-split failure messages emitted by both
 * bundlers this app has shipped:
 * - webpack: `ChunkLoadError` / "Loading chunk <id> failed"
 * - Vite / Rolldown: "Failed to fetch dynamically imported module" (Chromium),
 *   "Importing a module script failed" (Safari), "error loading dynamically
 *   imported module" (Firefox), and "Unable to preload CSS".
 *
 * The Vite variants are plain `TypeError`s (no `ChunkLoadError` name), so they
 * must be matched by message.
 */
const CHUNK_LOAD_ERROR_MESSAGE =
  /loading chunk [^ ]+ failed|failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload css/i;

/**
 * Whether an error is a failed dynamic-import / code-split chunk load.
 */
export const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error &&
  (error.name === 'ChunkLoadError' ||
    CHUNK_LOAD_ERROR_MESSAGE.test(error.message));

/**
 * Query param appended to the URL when we trigger a recovery reload, carrying
 * the attempt timestamp. Because it survives the full-page navigation it acts
 * as the durable recovery guard: unlike `sessionStorage`, it cannot be wiped
 * mid-loop. The logout / 401 flow clears `sessionStorage` on every page load
 * (see `clearStorage` in `authService`), so a stale chunk on `/logout` would
 * otherwise reset the storage guard and reload forever (~1×/sec).
 */
const CHUNK_RELOAD_PARAM = '_chunkReload';

/**
 * How long after a recovery reload further reloads stay suppressed. Within the
 * window a still-failing chunk logs "reload did not help" once and falls back
 * to the error-boundary UI; after it, a genuinely new incident (e.g. another
 * deploy while the tab stayed open) gets its own recovery attempt. A cooldown —
 * rather than retiring the marker as soon as the app boots — bounds the worst
 * case of an asset that is broken in the *current* deploy (extension-blocked,
 * corporate proxy, CDN outage) to one reload per window instead of an infinite
 * reload loop: lazy chunks fail *after* boot, so a boot-time clear would re-arm
 * recovery before the failure even recurs.
 */
export const CHUNK_RELOAD_COOLDOWN_MS = 5 * 60 * 1000;

/**
 * Grace period between deciding to reload and actually reloading. An import
 * cancelled by a document navigation surfaces *before* that navigation commits;
 * reloading immediately would cancel the user's navigation (the PROD-9410
 * sign-in hijack). Waiting lets the real navigation win the race — if the page
 * is still alive afterwards, the failure was not a navigation cancellation.
 * This is the primary protection on WebKit: iOS never fires `beforeunload`,
 * and desktop Safari is not documented to fire it reliably for same-origin
 * navigations either — so the window must comfortably cover a slow navigation
 * commit (server round-trip) that `pagehide` only signals at the end of.
 */
export const CHUNK_RELOAD_GRACE_MS = 1_000;

/**
 * How long the `beforeunload` navigation latch stays armed. `beforeunload` also
 * fires for navigations the user *cancels* via the leave-confirmation dialog
 * (e.g. closing the tab mid-call), and no event signals that cancellation — so
 * the latch must expire on its own, otherwise recovery (and its telemetry)
 * would stay silently disabled for the rest of the session. A navigation that
 * actually proceeds fires `pagehide` (a permanent latch) at commit.
 */
export const NAVIGATION_LATCH_RESET_MS = 10_000;

/**
 * Most recent recovery-reload timestamp, read from the URL marker (durable
 * across storage clears) and the `sessionStorage` marker (durable across
 * client-side navigations that drop the query string). Malformed values (e.g.
 * the legacy `'1'` storage flag) parse as ancient and therefore expired.
 */
const getLastReloadAttemptTimestamp = (): number | undefined => {
  let latest: number | undefined;
  try {
    const param = new URL(window.location.href).searchParams.get(
      CHUNK_RELOAD_PARAM,
    );
    if (param !== null) {
      latest = Number(param) || 0;
    }
  } catch {
    // ignore — fall through to the storage marker
  }
  try {
    const stored = window.sessionStorage.getItem(StorageKeys.ChunkReloaded);
    if (stored !== null) {
      latest = Math.max(latest ?? 0, Number(stored) || 0);
    }
  } catch {
    // ignore — storage may be unavailable (e.g. Safari private mode)
  }
  return latest;
};

/**
 * Whether a recovery reload was attempted recently (within
 * {@link CHUNK_RELOAD_COOLDOWN_MS}). While true, further reloads are suppressed
 * so a persistently failing chunk cannot loop the page; once the cooldown
 * elapses, a new incident may recover again.
 */
export const hasAttemptedChunkReload = (): boolean => {
  const attemptedAt = getLastReloadAttemptTimestamp();
  return (
    attemptedAt !== undefined &&
    Date.now() - attemptedAt < CHUNK_RELOAD_COOLDOWN_MS
  );
};

/**
 * Boot-time cleanup: strips the `_chunkReload` URL param and the
 * `sessionStorage` marker once the cooldown has elapsed, so an old marker does
 * not linger for the life of the tab (misreporting a much later, unrelated
 * incident as "reload did not help") and copied URLs stay clean. Markers still
 * inside the cooldown window are KEPT — they are the guard that stops a reload
 * loop when the reload did not fix the failure (`clearStorage` on `/logout`
 * makes `sessionStorage` alone insufficient). Best-effort — a lingering marker
 * is cosmetic, never correctness-critical.
 */
export const cleanupExpiredChunkReloadMarker = (): void => {
  if (hasAttemptedChunkReload()) {
    return;
  }
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has(CHUNK_RELOAD_PARAM)) {
      url.searchParams.delete(CHUNK_RELOAD_PARAM);
      window.history.replaceState(window.history.state, '', url.toString());
    }
  } catch {
    // ignore — a stale query param is harmless
  }
  try {
    window.sessionStorage.removeItem(StorageKeys.ChunkReloaded);
  } catch {
    // ignore — storage may be unavailable (e.g. Safari private mode)
  }
};

/**
 * Extracts the failing asset URL that some browsers embed in the failure
 * message, e.g.:
 * - Chromium: "Failed to fetch dynamically imported module: https://…/x.js"
 * - Firefox:  "error loading dynamically imported module: https://…/x.js"
 * - Vite CSS: "Unable to preload CSS for /v2/assets/x.css" (root-relative)
 *
 * Safari's "Importing a module script failed." carries no URL. Returns
 * `undefined` when none is present.
 */
const extractFailedChunkUrl = (error: unknown): string | undefined => {
  if (!(error instanceof Error)) {
    return undefined;
  }
  const absolute = error.message.match(/https?:\/\/[^\s"')]+/);
  if (absolute) {
    return absolute[0];
  }
  const rootRelative = error.message.match(/(?:^|[\s:])(\/[^\s"')]+)/);
  if (rootRelative) {
    try {
      return new URL(rootRelative[1], window.location.origin).toString();
    } catch {
      return undefined;
    }
  }
  return undefined;
};

/**
 * Verification outcome for a failed chunk:
 * - `missing`: confirmed 4xx/5xx — a stale/removed asset; a reload (which
 *   refetches `index.html` with the new hashed chunk names) can genuinely fix
 *   it.
 * - `reachable`: confirmed 2xx — the asset exists, so the import was cancelled
 *   by a navigation or blocked in-browser; a reload fixes nothing.
 * - `inconclusive`: the probe itself failed — offline, blocked, or aborted by
 *   the very navigation that cancelled the import. A reload cannot help an
 *   offline user and must not hijack an in-progress navigation, so this is
 *   skipped too.
 */
type ChunkVerification = 'missing' | 'reachable' | 'inconclusive';

/**
 * Probes the failing asset (HTTP cache bypassed) to verify the failure before
 * tearing the page down with a reload. Safari's error message carries no URL,
 * and probing anything else answers "am I online?", not "is my build stale?" —
 * so it is treated as `missing`, relying on the navigation latch and the reload
 * grace period to filter out cancelled imports.
 */
const verifyFailedChunk = async (
  error: unknown,
): Promise<ChunkVerification> => {
  const url = extractFailedChunkUrl(error);
  if (!url) {
    return 'missing';
  }
  try {
    const response = await fetch(url, { method: 'HEAD', cache: 'no-store' });
    return response.ok ? 'reachable' : 'missing';
  } catch {
    return 'inconclusive';
  }
};

/**
 * Set while a document navigation is believed to be in progress. An in-flight
 * dynamic import that the navigation cancels surfaces as a spurious chunk
 * failure — Safari/Firefox report "Importing a module script failed." / "error
 * loading dynamically imported module" for such aborts — so while set, both the
 * `vite:preloadError` handler and {@link attemptChunkReloadOnce} ignore every
 * failure entirely (no log, no reload). This is the dominant false positive: it
 * hijacked the redirect-trampoline routes (`/sign-in`, `/logout`, `/post-login`,
 * `/select-workspace`) that navigate away on mount.
 *
 * Armed permanently by `pagehide` (the document is genuinely going away;
 * undone only by a bfcache restore) and temporarily by `beforeunload` (see
 * {@link NAVIGATION_LATCH_RESET_MS} — the user may cancel the navigation via
 * the leave-confirmation dialog and no event reports that).
 */
let hasNavigationStarted = false;

let navigationLatchResetTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Monotonic count of page-lifecycle transitions (`pagehide`, `beforeunload`,
 * bfcache restore). A recovery decision captures it when it starts and aborts
 * the reload if it changed — the page navigated, or was suspended into the
 * back/forward cache and later restored, while the decision was pending.
 * Without this, a grace timer frozen in the bfcache would resume after a
 * Back-navigation restore — with {@link hasNavigationStarted} freshly cleared
 * by `pageshow` — and spuriously reload the restored page.
 */
let navigationEpoch = 0;

/**
 * Optional predicate reporting whether the user is currently in a live call.
 * When it returns `true`, {@link attemptChunkReloadOnce} skips the full-page
 * reload so a recoverable stale-chunk failure does not tear down an active
 * consultation; the retry + error-boundary fallback chain handles it instead.
 * Wired at startup via {@link setChunkReloadActiveCallGuard}; defaults to "not
 * in a call" so reload recovery stays enabled if the guard is never set.
 */
let isActiveCallGuard: (() => boolean) | undefined;

/**
 * Registers the predicate used to suppress chunk-reload recovery during a live
 * call. The latest guard wins. Kept injectable so this module stays decoupled
 * from the call feature and unit-testable in isolation.
 */
export const setChunkReloadActiveCallGuard = (guard: () => boolean): void => {
  isActiveCallGuard = guard;
};

/**
 * Throttles the active-call deferral branch. A chunk failure during a call can
 * recur across retries/remounts; report the deferral at most once per page load.
 */
let hasLoggedActiveCallDeferral = false;

/**
 * Whether a reload should be skipped because the user is in a live call.
 * Guard invocation is defensive: a throwing predicate must not block recovery.
 */
const shouldDeferReloadForActiveCall = (): boolean => {
  try {
    return isActiveCallGuard?.() === true;
  } catch {
    return false;
  }
};

/**
 * Single-flight guard: `true` while a recovery decision (verification fetch +
 * navigation grace) is pending. Several dynamically-imported modules routinely
 * fail together in one page load; the first failure owns the recovery decision
 * and the rest must be ignored rather than each logging its own
 * verification/outcome at the same millisecond. Reset once the decision
 * resolves so a genuinely *later* incident (SPA sessions never reload between
 * routes) can still recover.
 */
let isChunkRecoveryInFlight = false;

/**
 * Terminal flag set once a recovery reload has been triggered. The page is
 * navigating away, so every subsequent failure in this (doomed) page load is
 * ignored — no duplicate "reload did not help" noise before the reload lands.
 */
let hasTriggeredReload = false;

/**
 * Whether a recovery reload has been triggered in this page load (the
 * navigation is pending). Callers rendering fallback UI can keep showing a
 * blank/loading state instead of flashing an error page that the imminent
 * reload is about to replace.
 */
export const hasPendingRecoveryReload = (): boolean => hasTriggeredReload;

/**
 * Throttles the unrecoverable branch. Once a page has reloaded and the chunk
 * still fails, a render/remount loop can call {@link attemptChunkReloadOnce}
 * many times; log the unrecoverable state at most once per page load.
 */
let hasLoggedUnrecoverableChunkError = false;

/**
 * Performs the actual one-time full-page reload: records the attempt timestamp
 * in `sessionStorage` (best-effort) and in the `_chunkReload` URL param, then
 * navigates to the current URL. The changed query string forces a fresh
 * `index.html` fetch, so a genuine stale deploy resolves in a single reload.
 */
const triggerReload = (): void => {
  const now = Date.now();
  // Best-effort: the URL marker below is the authoritative loop guard, so a
  // throwing/full storage (e.g. Safari private mode) must not abort recovery.
  try {
    window.sessionStorage.setItem(StorageKeys.ChunkReloaded, String(now));
  } catch {
    // ignore — the URL marker still prevents a reload loop
  }
  const url = new URL(window.location.href);
  url.searchParams.set(CHUNK_RELOAD_PARAM, String(now));
  window.location.replace(url.toString());
};

/**
 * Resolves after {@link CHUNK_RELOAD_GRACE_MS}, giving a document navigation
 * that may have cancelled the failing import time to commit (which unloads this
 * page and moots the reload) or to fire `pagehide` / `beforeunload` (which arms
 * {@link hasNavigationStarted}).
 */
const waitForNavigationToSettle = (): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, CHUNK_RELOAD_GRACE_MS);
  });

/**
 * Attempts a single full-page reload to recover from a stale-chunk failure
 * (typically a deploy rotated chunk hashes while the tab was open), but only
 * after verifying the failure is a genuinely missing asset — not an import
 * cancelled by a navigation or blocked by an extension, which report the same
 * error yet are fixed by neither a reload nor anything else.
 *
 * Layered protections, in order:
 * 1. Navigation latch ({@link hasNavigationStarted}): failures during a
 *    navigation are ignored entirely (no log, no reload).
 * 2. Active-call deferral (see {@link setChunkReloadActiveCallGuard}).
 * 3. Single-flight: sibling failures in the same page load share one decision.
 * 4. Verification: the failing URL is probed; only a confirmed 4xx/5xx (or a
 *    Safari-style URL-less error) proceeds. A reachable asset or an
 *    inconclusive probe skips the reload.
 * 5. Cooldown ({@link hasAttemptedChunkReload}): at most one reload per
 *    {@link CHUNK_RELOAD_COOLDOWN_MS}, tracked primarily via the `_chunkReload`
 *    URL marker, which survives routes that wipe `sessionStorage`.
 * 6. Grace period ({@link CHUNK_RELOAD_GRACE_MS}): a navigation that cancelled
 *    the import gets time to win the race before we reload.
 *
 * @returns a promise resolving to `true` only when a reload was actually
 * triggered; `false` when the failure was ignored, deferred, skipped, or inside
 * the cooldown (so callers can render a fallback / let it propagate rather than
 * loop).
 */
export const attemptChunkReloadOnce = async (
  error: unknown,
): Promise<boolean> => {
  // A navigation cancelled the import: ignore it completely (no log, no
  // reload) — the page is going away.
  if (hasNavigationStarted) {
    return false;
  }
  // The page is already navigating away from a reload we triggered.
  if (hasTriggeredReload) {
    return false;
  }
  if (shouldDeferReloadForActiveCall()) {
    if (!hasLoggedActiveCallDeferral) {
      hasLoggedActiveCallDeferral = true;
      logger.warn(
        'chunk load failed during active call; deferring reload to avoid interrupting the call',
        error,
      );
    }
    return false;
  }
  // A sibling failure already owns the recovery decision for this page load.
  if (isChunkRecoveryInFlight) {
    return false;
  }
  isChunkRecoveryInFlight = true;
  const initialNavigationEpoch = navigationEpoch;
  try {
    const verification = await verifyFailedChunk(error);

    if (verification === 'reachable') {
      logger.warn('chunk load failed; asset reachable, skipping reload', error);
      return false;
    }
    if (verification === 'inconclusive') {
      logger.warn(
        'chunk load failed; verification inconclusive, skipping reload',
        error,
      );
      return false;
    }

    if (hasAttemptedChunkReload()) {
      if (!hasLoggedUnrecoverableChunkError) {
        hasLoggedUnrecoverableChunkError = true;
        logger.error('chunk load failed; reload did not help', error);
      }
      return false;
    }

    // Give a document navigation that may have cancelled this import time to
    // win the race — reloading now would cancel the user's navigation. The
    // epoch comparison also catches a bfcache suspend/restore cycle, where the
    // latch has already been reset by `pageshow`.
    await waitForNavigationToSettle();
    if (
      hasNavigationStarted ||
      hasTriggeredReload ||
      navigationEpoch !== initialNavigationEpoch
    ) {
      logger.warn(
        'chunk load failed; navigation started, skipping reload',
        error,
      );
      return false;
    }

    logger.error('chunk load failed; attempting recovery', error);
    hasTriggeredReload = true;
    triggerReload();
    return true;
  } finally {
    isChunkRecoveryInFlight = false;
  }
};

let isPreloadErrorHandlerRegistered = false;

/**
 * Vite / Rolldown dispatches a `vite:preloadError` event on `window` when a
 * dynamically-imported module (or one of its preloaded dependencies) fails to
 * load — typically because a deploy rotated the hashed asset filenames while
 * the tab was open. Recover with the same verified one-time reload the error
 * boundaries use.
 *
 * Also registers the `pagehide` / `beforeunload` listeners that arm the
 * navigation latch, so imports cancelled by an in-progress navigation are
 * ignored rather than misclassified as stale chunks.
 *
 * Idempotent: safe to call once at app startup.
 */
export const registerChunkPreloadErrorHandler = (): void => {
  if (isPreloadErrorHandlerRegistered || typeof window === 'undefined') {
    return;
  }
  isPreloadErrorHandlerRegistered = true;

  // Fires only when the document is genuinely being unloaded (or moved to the
  // bfcache) — a permanent latch, undone only by the bfcache restore below.
  window.addEventListener('pagehide', () => {
    hasNavigationStarted = true;
    navigationEpoch += 1;
  });
  // Fires at navigation *start* — early enough to beat the cancelled-import
  // rejection — but also for navigations the user cancels via the
  // leave-confirmation dialog (e.g. closing the tab mid-call), with no event
  // signalling the cancellation. Expire the latch so recovery is not silently
  // disabled for the rest of the session.
  window.addEventListener('beforeunload', () => {
    hasNavigationStarted = true;
    navigationEpoch += 1;
    if (navigationLatchResetTimer !== undefined) {
      clearTimeout(navigationLatchResetTimer);
    }
    navigationLatchResetTimer = setTimeout(() => {
      hasNavigationStarted = false;
    }, NAVIGATION_LATCH_RESET_MS);
  });
  // A page restored from the back/forward cache resumes the same JS context;
  // re-enable recovery for the restored session (while bumping the epoch so a
  // decision that was frozen mid-grace does not reload the restored page).
  window.addEventListener('pageshow', (event) => {
    if ((event as PageTransitionEvent).persisted) {
      hasNavigationStarted = false;
      navigationEpoch += 1;
    }
  });

  window.addEventListener('vite:preloadError', (event) => {
    // A navigation cancelled the import — the page is unloading and nothing
    // needs recovering.
    if (hasNavigationStarted) {
      return;
    }
    const preloadEvent = event as Event & { payload?: unknown };
    const reason = preloadEvent.payload ?? event;
    // Vite dispatches `vite:preloadError` for two very different failures: a
    // failed chunk *fetch* (stale hash after a deploy — recoverable by a
    // reload) and any error thrown while *evaluating* a lazily imported module
    // (Vite's preload helper wraps the import in `baseModule().catch(...)`).
    // Only fetch failures warrant a reload; a deterministic evaluation error
    // cannot be fixed by reloading and must not tear the page down. Concretely,
    // react-intl-tel-input's optional `import('libphonenumber-js-utils')` throws
    // on load and the library already swallows it, but reloading on that error
    // broke the SMS-invite flow. Gate on the same predicate the error
    // boundaries use, and let non-chunk errors fall through to Vite's default
    // rethrow so the owning code (or a boundary) handles them.
    if (!isChunkLoadError(reason)) {
      return;
    }
    // Recovery is async (verification + navigation grace), so Vite's default
    // rethrow cannot be cancelled here: `preventDefault` only counts during the
    // synchronous dispatch, and by the time the decision resolves Vite has
    // already rethrown. That is fine — the rejection propagates to the import's
    // caller / an error boundary, whose own `attemptChunkReloadOnce` call is
    // deduped by the single-flight and reload guards, and which owns the
    // fallback UI whenever we decide not to reload.
    void attemptChunkReloadOnce(reason);
  });
};
